import get from 'lodash/get';
import reduce from 'lodash/reduce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import cloneDeep from 'lodash/cloneDeep';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import * as CSV from 'csv-string';

import {
  inflect,
  formatDate,
  locationWithQuery,
  snakeCase,
  getClientLocaleAndCurrencyParams,
} from 'utils';
import {
  populateContentLookupValues,
  assignContentValuesForUpdate,
  contentValuesWithLocationData,
} from 'utils/content';
import { ORDER_CANCELABLE_FIELDS, isOrderCancelable } from 'utils/order-sales';

import api, { getLocalizedParams } from '../services/api';

import flash from './flash';
import content from './content';

const DEFAULT_LIMIT = 50;
const BULK_PAGE_LIMIT = 1000;

// List query keys that shape the page rather than which records match
const PAGE_QUERY_KEYS = [
  'page',
  'limit',
  'sort',
  'expand',
  'include',
  'fields',
  '$locale',
  '$currency',
];

export const DATA_FETCH_COLLECTION = 'data/fetchCollection';
export const DATA_FETCH_COLLECTION_META = 'data/fetchCollectionMeta';
export const DATA_FETCH_COLLECTION_COUNT = 'data/fetchCollectionCount';
export const DATA_FETCH_RECORD = 'data/fetchRecord';
export const DATA_REFRESH_RECORD = 'data/refreshRecord';
export const DATA_REQUEST_RECORD = 'data/requestRecord';
export const DATA_KEEP_FETCHED_RECORD = 'data/keepFetchedRecord';
export const DATA_FETCH_RELATED = 'data/fetchRelated';
export const DATA_PREFETCH_RELATED = 'data/prefetchRelated';
export const DATA_CLEAR_RECORD = 'data/clearRecord';
export const DATA_CLEAR_ERRORS = 'data/clearErrors';
export const DATA_CLEAR_QUERY = 'data/clearQuery';
export const DATA_UPDATE_RECORD = 'data/updateRecord';
export const DATA_UPDATE_FETCH_RECORD = 'data/updateFetchRecord';
export const DATA_CREATE_RECORD = 'data/createRecord';
export const DATA_DELETE_RECORD = 'data/deleteRecord';
export const DATA_SELECT_ALL = 'data/selectAll';
export const DATA_SELECT_ONE = 'data/selectOne';
export const DATA_SELECT_CLEAR = 'data/selectClear';
export const DATA_BULK_START = 'data/bulkStart';
export const DATA_BULK_PROGRESS = 'data/bulkProgress';
export const DATA_BULK_CANCEL = 'data/bulkCancel';
export const DATA_BULK_COMPLETE = 'data/bulkComplete';
export const DATA_INTEGRATION_SYNC_WATCH = 'data/integrationSyncWatch';
export const DATA_INTEGRATION_SYNC_PROCESSING =
  'data/integrationSyncProcessing';
export const DATA_INTEGRATION_SYNC_FAILED = 'data/integrationSyncFailed';
export const DATA_INTEGRATION_SYNC_COMPLETE = 'data/integrationSyncComplete';
export const DATA_LOADING = 'data/loading';
export const DATA_LOADING_RELATED = 'data/loadingRelated';
export const DATA_PATCH_RECORD = 'data/patchRecord';

function fillBatchSubRequest(batch, method, data) {
  batch = { ...batch };

  if (!batch.method) {
    batch.method = method;
  }

  if (data) {
    if (!batch.data) {
      batch.data = {};
    }

    Object.assign(batch.data, data);
  }

  return batch;
}

function fillBatchData(batch, method, data) {
  if (Array.isArray(batch)) {
    return batch.map((query) => fillBatchSubRequest(query, method, data));
  }

  batch = { ...batch };

  for (const [key, query] of Object.entries(batch)) {
    batch[key] = fillBatchSubRequest(query, method);
  }

  Object.assign(batch, data);

  return batch;
}

/**
 * this function replaces the content model name if this is a draft order:
 * we show the "orders" content for draft orders when model is "carts".
 * therefore, we should update the "orders" content.
 * @param {string} model - model name
 * @param {object} record - current record
 * @returns {string} - content model name to update
 */
function getRecordContentModel(model, record) {
  if (model === 'carts' && record?.draft) {
    return 'orders';
  }

  return model;
}

/**
 * Dispatches a request that fetches or saves the record and counts it while
 * it runs. A background refresh that overlaps such a request is dropped, so
 * the page keeps what the request got
 *
 * @param {Function} dispatch
 * @param {object} action
 * @returns {Promise<any>}
 */
async function requestRecord(dispatch, action) {
  dispatch({ type: DATA_REQUEST_RECORD });
  try {
    return await dispatch(action);
  } finally {
    dispatch({ type: DATA_REQUEST_RECORD, payload: { done: true } });
  }
}

const actions = {
  fetchCollection(
    model,
    query = {},
    locationQuery = {},
    filterQuery = {},
    endpoint = undefined,
  ) {
    return (dispatch) => {
      query.limit =
        query.limit ||
        window.localStorage.getItem(`${model}_per_page`) ||
        DEFAULT_LIMIT;

      const meta = { model, query: { ...query }, locationQuery, filterQuery };

      dispatch({
        type: DATA_FETCH_COLLECTION_META,
        payload: meta,
      });

      return dispatch({
        type: DATA_FETCH_COLLECTION,
        payload: endpoint
          ? api.get(endpoint, query)
          : api.post(`/data/$get/${model}`, query),
        meta: meta,
        showLoading: [':logs'].includes(model) ? false : true,
      });
    };
  },

  getCollectionSeriesQuery(queryModel) {
    return (_dispatch, getState) => {
      const {
        data: { model, filterQuery },
      } = getState();
      if (model !== queryModel) {
        return {};
      }
      return filterQuery;
    };
  },

  fetchCollectionCount(model, endpoint = undefined, baseQuery = {}) {
    return {
      type: DATA_FETCH_COLLECTION_COUNT,
      payload: api.get(endpoint || `/data/${model}/:count`, baseQuery),
    };
  },

  /**
   * @param {string} model
   * @param {string} id
   * @param {object} [query]
   * @param {object} [contentModel]
   * @param {object} [options]
   * @param {boolean} [options.keepFetched] keeps a copy of the API response, so
   * a later refresh can tell whether anything changed
   */
  fetchRecord(model, id, query = {}, contentModel, { keepFetched } = {}) {
    return async (dispatch) => {
      let fetched;

      const result = await requestRecord(dispatch, {
        type: DATA_FETCH_RECORD,
        payload: api
          .getLocalized(`/data/${model}/${id}`, {
            ...query,
          })
          .then(async (record) => {
            if (!record && global.router?.didChangeEnvironment) {
              this.redirectToCollectionList(global.router);
            }
            if (record && keepFetched) {
              fetched = cloneDeep(record);
            }
            await dispatch(
              this.fetchRecordContent(model, record, contentModel),
            );
            return record;
          }),
        meta: { model, id, query },
      });

      if (result && fetched) {
        dispatch({
          type: DATA_KEEP_FETCHED_RECORD,
          payload: { record: result, data: fetched },
        });
      }

      return result;
    };
  },

  /**
   * Fetches the shown record again in the background, with the same query,
   * and shows what changed since, such as values saved by apps
   *
   * @param {string} model
   * @param {string} id the record's id, or the order's number, as in the URL
   * @param {object} [options]
   * @param {string[]} [options.included] child collections, such as payments,
   * whose content values are fetched, as on the page load
   * @param {string[]} [options.skipIncludes] includes not fetched again, as
   * saving the record doesn't change them, such as the prev and next records.
   * The shown values are kept
   * @param {(record: object) => void} [options.prepare] the same preparation
   * the page applies after its own fetch
   * @param {boolean | (() => boolean)} [options.appsOnly] takes only the apps'
   * values ($app), also of child records, and keeps the rest as shown, such as
   * while the user edits. A function is called after the fetch, as the user
   * can start editing meanwhile
   * @returns {Promise<{ record: object, onlyApps: boolean } | undefined>} the
   * new record and whether only the apps' values changed, or undefined when
   * the shown record didn't change
   */
  refreshRecord(
    model,
    id,
    { included = [], skipIncludes = [], prepare, appsOnly = false } = {},
  ) {
    return async (dispatch, getState) => {
      const { record, recordQuery, fetchedRecord, recordRequests } =
        getState().data;

      // Another record is shown. The id can be the order's number
      if (!record || (record.id !== id && String(record.number) !== id)) {
        return;
      }

      const query = {
        ...recordQuery,
        ...(recordQuery?.include && {
          include: omit(recordQuery.include, skipIncludes),
        }),
      };

      const isAppsOnly = () =>
        typeof appsOnly === 'function' ? appsOnly() : appsOnly;

      let nextRecord;
      let fetched;

      try {
        nextRecord = await api.getLocalized(`/data/${model}/${id}`, query);

        // The API returned the same as last time, so the content values aren't
        // fetched again. Unless last time only the apps' values were taken,
        // and now everything is
        if (
          !nextRecord ||
          (fetchedRecord?.record === record &&
            isEqual(omit(fetchedRecord.data, skipIncludes), nextRecord) &&
            (!fetchedRecord.appsOnly || isAppsOnly()))
        ) {
          return;
        }

        fetched = cloneDeep(nextRecord);

        // The skipped includes stay as shown. Set on nextRecord itself, not a
        // copy, as child records can refer back to it (a payment to its order)
        Object.assign(nextRecord, pick(record, skipIncludes));

        await dispatch(this.fetchRecordContent(model, nextRecord));
        await dispatch(this.fetchIncludedContent(included, nextRecord));
        prepare?.(nextRecord);
      } catch (err) {
        // The shown record stays, and the next refresh tries again
        console.error(err);
        return;
      }

      const onlyApps = isAppsOnly();
      const payload = onlyApps
        ? mergeAppValues(record, nextRecord)
        : nextRecord;

      dispatch({
        type: DATA_REFRESH_RECORD,
        payload,
        // fetched lets the next refresh skip when nothing changed. appsOnly
        // tells it that only the apps' values of fetched are shown
        meta: { record, recordRequests, fetched, appsOnly: onlyApps },
      });

      if (getState().data.refreshedRecord !== payload) {
        return undefined;
      }

      return {
        record: payload,
        onlyApps: onlyApps || hasOnlyAppChanges(record, payload),
      };
    };
  },

  redirectToCollectionList(router) {
    const { id } = router.params;
    const nextUrl = router.location.pathname.replace(
      new RegExp(`/${id}.*`),
      '',
    );
    router.replace(nextUrl);
  },

  fetchRelated(relatedId, relatedQuery, { showError } = {}) {
    if (isEmpty(relatedQuery)) {
      return Promise.resolve(Array.isArray(relatedQuery) ? [] : {});
    }

    return (dispatch) => {
      dispatch(this.loadingRelated(true));
      dispatch({
        type: DATA_PREFETCH_RELATED,
        meta: { relatedId },
      });
      return dispatch({
        type: DATA_FETCH_RELATED,
        payload: api.post(
          '/data/:batch',
          fillBatchData(relatedQuery, 'get', getLocalizedParams(relatedQuery)),
        ),
        meta: { relatedId, showLoading: false },
        showError,
      }).then((result) => {
        dispatch(this.loadingRelated(false));
        return result;
      });
    };
  },

  clearRecord() {
    return {
      type: DATA_CLEAR_RECORD,
    };
  },

  clearErrors() {
    return {
      type: DATA_CLEAR_ERRORS,
    };
  },

  clearQuery() {
    return {
      type: DATA_CLEAR_QUERY,
    };
  },

  loading(payload) {
    return { type: DATA_LOADING, payload };
  },

  loadingRelated(payload) {
    return { type: DATA_LOADING_RELATED, payload };
  },

  updateRecord(
    model,
    id,
    data,
    updateContent = true,
    contentModel,
    contentRecord,
  ) {
    return async (dispatch) => {
      const update = updateContent
        ? await dispatch(
            this.updateRecordContent(
              contentModel || model,
              id,
              data,
              contentRecord,
            ),
          )
        : data;

      return requestRecord(dispatch, {
        type: DATA_UPDATE_RECORD,
        payload: api.put(`/data/${model}/${id}`, update),
        meta: { model, id },
      });
    };
  },

  updateFetchRecord(model, id, data, contentModel) {
    return async (dispatch, getState) => {
      const { query } = getState().data;
      const update = await dispatch(
        this.updateRecordContent(contentModel || model, id, data),
      );
      return requestRecord(dispatch, {
        type: DATA_UPDATE_FETCH_RECORD,
        payload: api
          .put(`/data/${model}/${id}`, update)
          .then((result) => {
            if (result && result.errors) {
              return result;
            }
            return api.getLocalized(`/data/${model}/${id}`, query);
          })
          .then(async (record) => {
            await dispatch(
              this.fetchRecordContent(model, record, contentModel),
            );
            return record;
          }),
        meta: { model, id },
      });
    };
  },

  patchRecord(payload) {
    return async (dispatch) => {
      return dispatch({
        type: DATA_PATCH_RECORD,
        payload,
      });
    };
  },

  fetchRecordContent(model, record, contentModel) {
    return async (dispatch, getState) => {
      const { user, partner } = getState();

      if (!record || user.superAdmin || partner) {
        return record;
      }

      const contentModels = await dispatch(
        content.getModelsByCollection(contentModel || model),
      );
      if (contentModels.length > 0) {
        await populateContentLookupValues(record, contentModels);
      }
    };
  },

  fetchIncludedContent(models, result) {
    return async (dispatch, getState) => {
      const { superAdmin } = getState().user;
      if (superAdmin) return;
      const promises = [];
      for (const model of models) {
        await dispatch(content.getModelsByCollection(model));
        if (result[model] && result[model].results) {
          promises.push(
            Promise.all(
              result[model].results.map((record) =>
                dispatch(this.fetchRecordContent(model, record)),
              ),
            ),
          );
        }
      }
      await Promise.all(promises);
    };
  },

  updateRecordContent(model, id, data, contentRecord) {
    return async (dispatch, getState) => {
      const { user, partner } = getState();

      if (!data || user.superAdmin || partner) return data;

      const { record } = getState().data;
      const contentModel = getRecordContentModel(model, record);

      const contentModels = await dispatch(
        content.getModelsByCollection(contentModel),
      ).then((models) => models.filter((model) => !model.standard));

      if (contentModels.length > 0) {
        const { model: exModel } = getState().data;
        const exContentModel = getRecordContentModel(exModel, record);
        const updateRecord =
          contentModel === exContentModel
            ? contentRecord || record
            : get(record && record[contentModel], 'results', []).find(
                (rec) => rec.id === id,
              );
        const contentData = assignContentValuesForUpdate(
          data,
          updateRecord,
          contentModels,
        );
        return contentData;
      }
      return data;
    };
  },

  createRecordContent(model, data) {
    return async (dispatch, getState) => {
      const { user, partner } = getState();

      if (!data || user.superAdmin || partner) return data;

      const modelsByCollection = await dispatch(
        content.getModelsByCollection(model),
      );
      const contentModels = modelsByCollection.filter(
        (model) => !model.standard,
      );

      if (contentModels.length > 0) {
        const newData = assignContentValuesForUpdate(
          data,
          undefined,
          contentModels,
        );
        return {
          ...newData,
          ...newData.$set,
          $set: undefined,
        };
      }
      return data;
    };
  },

  getValuesWithData(model, data) {
    return async (dispatch) => {
      const values = { ...(data || undefined) };
      if (data) {
        const contentModels = await dispatch(
          content.getModelsByCollection(model),
        );
        if (contentModels.length > 0) {
          await populateContentLookupValues(values, contentModels);
        }
      }
      return values;
    };
  },

  getValuesWithLocationData(model, location) {
    return async (dispatch) => {
      const values = contentValuesWithLocationData(location);
      if (location?.query?.record) {
        const contentModels = await dispatch(
          content.getModelsByCollection(model),
        );
        if (contentModels.length > 0) {
          await populateContentLookupValues(values, contentModels);
        }
      }
      return values;
    };
  },

  createRecord(model, data, contentModel) {
    return async (dispatch) => {
      const newData = await dispatch(
        this.createRecordContent(contentModel || model, data),
      );
      return requestRecord(dispatch, {
        type: DATA_CREATE_RECORD,
        payload: api.post(`/data/${model}`, newData),
        meta: { model },
      });
    };
  },

  deleteRecord(model, id) {
    return (dispatch) =>
      requestRecord(dispatch, {
        type: DATA_DELETE_RECORD,
        payload: api.delete(`/data/${model}/${id}`, { $force_delete: true }),
        meta: { model },
      });
  },

  selectAll() {
    return {
      type: DATA_SELECT_ALL,
    };
  },

  selectOne(id, isRange) {
    return {
      type: DATA_SELECT_ONE,
      payload: id,
      meta: { isRange },
    };
  },

  selectClear() {
    return {
      type: DATA_SELECT_CLEAR,
    };
  },

  integrationSyncFailed(params) {
    return {
      type: DATA_INTEGRATION_SYNC_FAILED,
      payload: params,
    };
  },

  integrationSyncWatch(integration, watch = true) {
    return {
      type: DATA_INTEGRATION_SYNC_WATCH,
      payload: integration,
      meta: {
        watch,
      },
    };
  },

  integrationSyncProcessing(integration, message) {
    return {
      type: DATA_INTEGRATION_SYNC_PROCESSING,
      payload: integration,
      meta: { message },
    };
  },

  integrationSyncComplete(integration, message) {
    return {
      type: DATA_INTEGRATION_SYNC_COMPLETE,
      payload: integration,
      meta: {
        message,
      },
    };
  },

  bulkStart(params) {
    return {
      type: DATA_BULK_START,
      payload: params,
    };
  },

  bulkProgress(percent, message) {
    return {
      type: DATA_BULK_PROGRESS,
      payload: percent,
      meta: { message },
    };
  },

  bulkCancel() {
    return {
      type: DATA_BULK_CANCEL,
      payload: null,
    };
  },

  bulkComplete(result) {
    return {
      type: DATA_BULK_COMPLETE,
      payload: result,
    };
  },

  bulkExport(model, params) {
    return (dispatch, getState) => {
      if (getState().data.bulk.running) {
        return;
      }

      dispatch(this.bulkStart({ model, ...params }));

      return runExport({
        getState,
        model,
        params,
        onProgress: (percent, message) => {
          dispatch(this.bulkProgress(percent, message));
        },
        onCancel: () => {
          dispatch(this.bulkCancel());
        },
        onComplete: (result) => {
          dispatch(this.bulkComplete(result));
        },
        onError: (message) => {
          dispatch(this.bulkCancel());
          dispatch(flash.error(message));
        },
      });
    };
  },

  bulkImport(model, params) {
    return (dispatch, getState) => {
      if (getState().data.bulk.running) {
        return;
      }

      dispatch(this.bulkStart({ model, ...params }));

      return runImport({
        getState,
        model,
        params,
        onProgress: (percent, message) => {
          dispatch(this.bulkProgress(percent, message));
        },
        onCancel: () => {
          dispatch(this.bulkCancel());
        },
        onComplete: (result) => {
          dispatch(this.bulkComplete(result));
        },
        onError: (message) => {
          dispatch(this.bulkCancel());
          dispatch(flash.error(message));
        },
      });
    };
  },

  bulkUpdateSelected(model, handler) {
    return (dispatch, getState) => {
      if (getState().data.bulk.running) {
        return;
      }

      dispatch(this.bulkStart({ model, handler }));

      return runUpdateSelected({
        getState,
        model,
        handler,
        onProgress: (percent, message) => {
          dispatch(this.bulkProgress(percent, message));
        },
        onCancel: () => {
          dispatch(this.bulkCancel());
        },
        onComplete: (result) => {
          dispatch(this.bulkComplete(result));
        },
        onError: (message) => {
          dispatch(this.bulkCancel());
          dispatch(flash.error(message));
        },
      });
    };
  },

  bulkDeleteSelected(
    model,
    query = undefined,
    bulkParams = undefined,
    cancelOrders = false,
  ) {
    return (dispatch, getState) => {
      if (getState().data.bulk.running) {
        return;
      }

      dispatch(this.bulkStart({ model }));

      return runDeleteSelected({
        getState,
        model,
        query,
        bulkParams,
        cancelOrders,
        onProgress: (percent, message) => {
          dispatch(this.bulkProgress(percent, message));
        },
        onNotDeleted: (count, notCanceledCount) => {
          dispatch(
            flash.error(
              `${inflect(count, model)} couldn't be deleted${
                notCanceledCount > 0
                  ? `, ${notCanceledCount} of them couldn't be canceled`
                  : ''
              }`,
            ),
          );
        },
        onCancel: () => {
          dispatch(this.bulkCancel());
        },
        onComplete: (result) => {
          dispatch(this.selectClear());
          dispatch(this.bulkComplete(result));
        },
        onError: (message) => {
          dispatch(this.bulkCancel());
          dispatch(flash.error(message));
        },
      });
    };
  },

  // Count selected records matching the query
  bulkCountSelected(model, query) {
    return async (dispatch, getState) => {
      const bulkQuery = getBulkQuery(getState, 'id');

      if (!bulkQuery) {
        return 0;
      }

      const { data: result } = await api.post('/data/:batch', {
        data: {
          method: 'get',
          url: model,
          data: {
            ...bulkQuery,
            $and: [...(bulkQuery.$and || []), query],
            fields: 'id',
            limit: 1,
          },
        },
      });

      return result?.count || 0;
    };
  },

  bulkGenerateCouponCodes(couponId, count) {
    return async (dispatch, getState) => {
      if (getState().data.bulk.running) {
        return;
      }

      dispatch(this.bulkStart({ couponId, count }));

      const gen = await api.post(`/data/coupons/${couponId}/generations`, {
        count,
      });

      return runCouponCodeGenerationProgress(gen, {
        onProgress: (percent, message) => {
          dispatch(this.bulkProgress(percent, message));
        },
        onComplete: (result) => {
          dispatch(this.bulkComplete(result));
        },
        onError: (message) => {
          dispatch(this.bulkCancel());
          dispatch(flash.error(message));
        },
      });
    };
  },

  startIntegrationSync(integrationId) {
    if (!integrationId) {
      return flash.error('Provide integration ID');
    }

    return async (dispatch, getState) => {
      const event = await api.post('/data/events', {
        data: {
          id: integrationId,
        },
        model: ':webhooks',
        type: 'integration.sync_processing',
      });

      if (!event) {
        dispatch(flash.error('Unable to start sync process'));
        return;
      } else if (event.errors) {
        dispatch(flash.error(Object.keys(event.errors)[0].message));
        return;
      } else if (event.error) {
        dispatch(flash.error(event.error));
        return;
      }

      dispatch(this.integrationSyncProcessing(integrationId));
      dispatch(this.integrationSyncWatch(integrationId, true));

      return runIntegrationProgressCheck(integrationId, {
        getState,
        onProcessing: () => {
          dispatch(this.integrationSyncProcessing(integrationId));
        },
        onComplete: () => {
          dispatch(this.integrationSyncComplete(integrationId));
        },
        onError: (message) => {
          dispatch(this.integrationSyncFailed(integrationId));
          dispatch(flash.error(message));
        },
      });
    };
  },

  watchIntegrationSync(integrationId, watch = true) {
    return async (dispatch, getState) => {
      const { integrationSync } = getState().data;
      dispatch(this.integrationSyncWatch(integrationId, watch));
      if (watch && !integrationSync[integrationId].watch) {
        return runIntegrationProgressCheck(integrationId, {
          getState,
          onProcessing: () => {
            dispatch(this.integrationSyncProcessing(integrationId));
          },
          onComplete: () => {
            dispatch(this.integrationSyncComplete(integrationId));
          },
          onError: (message) => {
            dispatch(this.integrationSyncFailed(integrationId));
            dispatch(flash.error(message));
          },
        });
      }
    };
  },

  bulkGenerateGiftCards(productId, counts, description) {
    return async (dispatch, getState) => {
      if (getState().data.bulk.running) {
        return;
      }

      const actualCounts = counts
        .map((count) => count && count.count > 0 && count.amount > 0 && count)
        .filter((count) => count);

      dispatch(this.bulkStart({ productId, counts }));

      try {
        await Promise.all(
          actualCounts.map((count) =>
            api.post('/data/giftcards', {
              $bulk_count: count.count,
              amount: count.amount,
              $currency: count.$currency,
              product_id: productId,
              bulk_description: description,
            }),
          ),
        );
        dispatch(this.bulkComplete({}));
      } catch (err) {
        dispatch(this.bulkCancel());
        dispatch(flash.error(err.message));
      }

      dispatch(this.bulkComplete({}));
    };
  },
};

appendChildActions();

export default actions;

export const initialState = {
  model: null,
  collection: null,
  collectionCount: 0,
  query: {},
  locationQuery: null,
  filterQuery: null,
  related: {},
  relatedId: null,
  record: null,
  // The record the last background refresh set, to tell it from other fetches
  refreshedRecord: null,
  // The query the record was fetched with. state.query is changed by lists
  recordQuery: {},
  // A copy of the API response for the shown record, before content values
  // were added: { record, data, appsOnly }. A refresh compares with it to skip
  // fetching content values when nothing changed
  fetchedRecord: null,
  // Fetches and saves of the record started so far. A refresh started before
  // one of them is dropped, as its data is older
  recordRequests: 0,
  // Fetches and saves of the record running now. A refresh that ends
  // meanwhile is dropped, as the request can get newer data
  pendingRecordRequests: 0,
  recordErrors: {},
  selection: {
    all: false,
    records: {},
    except: {},
    count: 0,
    lastId: null,
  },
  bulk: {
    params: {},
    running: false,
    percent: 0,
    complete: false,
    result: {},
    message: undefined,
  },
  integrationSync: {
    // [integration]: { status, watch }
  },
  loading: false,
  loadingRelated: false,
  loadingCollection: false,

  // Child collections
  children: {},
  initialState() {
    return { ...initialState };
  },
};

/**
 * Whether the refetched record is the same as the shown one. Nested records,
 * such as an expanded product, are compared by date_updated only, as separate
 * fetches can build them differently. Records in lists, such as payments, are
 * compared in full, as their included data, such as refunds, can change
 * without changing their date_updated
 *
 * @param {object} record
 * @param {object} nextRecord
 * @returns {boolean}
 */
export function isSameRecord(record, nextRecord) {
  return isEqualWith(record, nextRecord, (value, nextValue, key, parent) =>
    key !== undefined &&
    !Array.isArray(parent) &&
    isPlainObject(value) &&
    isPlainObject(nextValue) &&
    value.id !== undefined &&
    value.id === nextValue.id &&
    value.date_updated &&
    nextValue.date_updated
      ? value.date_updated === nextValue.date_updated
      : undefined,
  );
}

/**
 * The shown record with the apps' values ($app) of the refetched one, also for
 * child records: items, included collections, expanded records. Child records
 * are matched by id, and everything else stays as shown. Unchanged objects
 * keep their references, so forms don't reload their values
 *
 * @param {object} record
 * @param {object} nextRecord
 * @param {Map<object, object>} [mergedRecords] records merged already, for
 * child records that refer back to their parent, such as a payment to its
 * order
 * @param {Set<object>} [merging] records being merged now. A reference back
 * to one of them isn't a change
 * @returns {object}
 */
export function mergeAppValues(
  record,
  nextRecord,
  mergedRecords = new Map(),
  merging = new Set(),
) {
  if (!isPlainObject(record) || !isPlainObject(nextRecord)) {
    return record;
  }

  if (mergedRecords.has(record)) {
    return mergedRecords.get(record);
  }

  const merged = { ...record };
  mergedRecords.set(record, merged);
  merging.add(record);

  let changed = !isEqual(record.$app, nextRecord.$app);
  const isChanged = (value, mergedValue) =>
    mergedValue !== value && !merging.has(value);

  if ('$app' in nextRecord) {
    merged.$app = nextRecord.$app;
  } else {
    delete merged.$app;
  }

  for (const [key, value] of Object.entries(record)) {
    const nextValue = nextRecord[key];

    if (key === '$app' || nextValue === undefined) {
      continue;
    }

    if (Array.isArray(value) && Array.isArray(nextValue)) {
      merged[key] = value.map((item) => {
        const nextItem =
          item?.id !== undefined &&
          nextValue.find((next) => next?.id === item.id);
        return nextItem
          ? mergeAppValues(item, nextItem, mergedRecords, merging)
          : item;
      });
      if (merged[key].some((item, index) => isChanged(value[index], item))) {
        changed = true;
      } else {
        merged[key] = value;
      }
    } else if (
      isPlainObject(value) &&
      (value.id === undefined || value.id === nextValue?.id)
    ) {
      merged[key] = mergeAppValues(value, nextValue, mergedRecords, merging);
      changed = changed || isChanged(value, merged[key]);
    }
  }

  merging.delete(record);

  if (!changed) {
    mergedRecords.set(record, record);
    return record;
  }

  return merged;
}

/**
 * Whether the refetched record differs from the shown one only in the apps'
 * values ($app), also of child records
 *
 * @param {object} record
 * @param {object} nextRecord
 * @returns {boolean}
 */
export function hasOnlyAppChanges(record, nextRecord) {
  // An app's save changes date_updated too, so it isn't compared
  return isEqualWith(
    mergeAppValues(record, nextRecord),
    nextRecord,
    (value, nextValue, key) => (key === 'date_updated' ? true : undefined),
  );
}

export function reducer(state = initialState, action) {
  const childId = action.meta?.childId;

  if (childId) {
    const nextState = {
      ...state,
      children: {
        ...state.children,
        [childId]: reduceState(state.children[childId], action),
      },
    };

    const loadingCollection = reduce(
      nextState.children,
      (acc, childState) => acc || childState.loadingCollection,
      false,
    );

    return {
      ...nextState,
      loadingCollection,
    };
  }

  return reduceState(state, action);
}

function reduceState(state = initialState, action) {
  switch (action.type) {
    case 'RESET':
      return { ...initialState };

    case DATA_FETCH_COLLECTION: {
      const result = action.payload;
      if (action.meta === undefined) {
        return state;
      }
      if (state.model !== action.meta.model) {
        console.warn(
          `DATA_FETCH_COLLECTION expected '${state.model}' but received '${action.meta.model}'`,
        );
        return state;
      }
      return {
        ...state,
        ...action.meta,
        loadingCollection: false,
        collection: {
          ...result,
          totalPages: Object.keys(result?.pages || {}).length,
          limit: action.meta.query.limit,
          limit_count: action.meta.query.limit_count,
        },
        query: {
          ...(action.meta.query || {}),
        },
        selection: {
          ...state.selection,
          count: getSelectionCount(state.selection, result),
        },
      };
    }

    case DATA_FETCH_COLLECTION_META:
      return {
        ...state,
        ...action.payload,
        ...(state.model !== action.payload.model
          ? {
              query: {},
              selection: {
                ...initialState.selection,
              },
            }
          : undefined),
        loadingCollection: true,
      };

    case DATA_FETCH_COLLECTION_COUNT:
      return {
        ...state,
        collectionCount: action.payload || 0,
      };

    case DATA_FETCH_RECORD:
      return {
        ...state,
        ...action.meta,
        record: action.payload,
        recordQuery: action.meta.query,
        recordErrors: {},
      };

    case DATA_REQUEST_RECORD:
      return action.payload?.done
        ? {
            ...state,
            pendingRecordRequests: (state.pendingRecordRequests || 0) - 1,
          }
        : {
            ...state,
            recordRequests: (state.recordRequests || 0) + 1,
            pendingRecordRequests: (state.pendingRecordRequests || 0) + 1,
          };

    case DATA_KEEP_FETCHED_RECORD:
      return {
        ...state,
        fetchedRecord: action.payload,
      };

    case DATA_REFRESH_RECORD: {
      const { record, recordRequests, fetched, appsOnly } = action.meta;

      // Dropped if the record was fetched or saved since the refresh started,
      // or is being fetched or saved now, as that data is newer. The same
      // record keeps the state, so the page doesn't render
      if (
        state.record !== record ||
        state.recordRequests !== recordRequests ||
        state.pendingRecordRequests > 0
      ) {
        return state;
      }

      if (isSameRecord(state.record, action.payload)) {
        return fetched
          ? { ...state, fetchedRecord: { record, data: fetched, appsOnly } }
          : state;
      }

      return {
        ...state,
        record: action.payload,
        // So the page can tell a refresh from a fetch after a save or action
        refreshedRecord: action.payload,
        fetchedRecord: fetched
          ? { record: action.payload, data: fetched, appsOnly }
          : state.fetchedRecord,
      };
    }

    case DATA_PREFETCH_RELATED: {
      const relatedId = action.meta?.relatedId ?? null;

      return {
        ...state,
        related:
          !relatedId || relatedId !== state.relatedId ? {} : state.related,
        relatedId,
      };
    }

    case DATA_FETCH_RELATED:
      if (!action.payload) {
        return state;
      }
      if (action.meta.relatedId !== state.relatedId) {
        return state;
      }
      return {
        ...state,
        related: {
          ...state.related,
          ...action.payload,
        },
      };

    case DATA_CLEAR_RECORD:
      return {
        ...state,
        record: null,
        refreshedRecord: null,
        recordQuery: {},
        fetchedRecord: null,
        recordErrors: {},
        related: {},
        relatedId: null,
      };

    case DATA_CLEAR_ERRORS:
      return {
        ...state,
        recordErrors: {},
      };

    case DATA_CLEAR_QUERY:
      return {
        ...state,
        query: {},
      };

    case DATA_CREATE_RECORD:
    case DATA_UPDATE_RECORD:
      if (action.payload && action.payload.errors) {
        return {
          ...state,
          recordErrors: action.payload.errors,
        };
      }
      return state;

    case DATA_UPDATE_FETCH_RECORD:
      if (action.payload && action.payload.errors) {
        return {
          ...state,
          recordErrors: action.payload.errors,
        };
      }
      return {
        ...state,
        record: action.payload,
      };

    case DATA_DELETE_RECORD:
      if (action.payload && action.payload.errors) {
        return {
          ...state,
          recordErrors: action.payload.errors,
          selection: {
            ...initialState.selection,
          },
        };
      }
      return state;

    case DATA_SELECT_ALL:
      return {
        ...state,
        selection: {
          all: true,
          records: {},
          except: {},
          count: state.collection.count,
          lastId: null,
        },
      };

    case DATA_SELECT_ONE: {
      if (!action.payload) {
        return state;
      }
      const id = action.payload;
      const sel = state.selection;
      const { all } = sel;
      let records = { ...sel.records };
      let except = { ...sel.except };
      let range;
      if (action.meta.isRange && sel.lastId) {
        range = getSelectionRange(sel.lastId, id, state.collection);
      }
      if (all) {
        if (except[id]) {
          delete except[id];
          if (range) {
            for (const key in range) {
              delete except[key];
            }
          }
        } else {
          except[id] = true;
          if (range) {
            except = { ...except, ...range };
          }
        }
      } else {
        if (records[id]) {
          delete records[id];
          if (range) {
            for (const key in range) {
              delete records[key];
            }
          }
        } else {
          records[id] = true;
          if (range) {
            records = { ...records, ...range };
          }
        }
      }
      return {
        ...state,
        selection: {
          all,
          records,
          except,
          count: getSelectionCount({ all, records, except }, state.collection),
          lastId: id,
        },
      };
    }

    case DATA_SELECT_CLEAR:
      return {
        ...state,
        selection: {
          all: false,
          records: {},
          except: {},
          count: 0,
          lastId: null,
        },
      };

    case DATA_BULK_START:
      return {
        ...state,
        bulk: {
          ...initialState.bulk,
          running: true,
          params: action.payload,
        },
      };

    case DATA_INTEGRATION_SYNC_WATCH: {
      const status = get(state.integrationSync[action.payload], 'status');
      return {
        ...state,
        integrationSync: {
          ...state.integrationSync,
          [action.payload]: {
            ...state.integrationSync[action.payload],
            status: action.meta.watch ? status : null,
            watch: action.meta.watch,
          },
        },
      };
    }

    case DATA_INTEGRATION_SYNC_PROCESSING: {
      return {
        ...state,
        integrationSync: {
          ...state.integrationSync,
          [action.payload]: {
            ...state.integrationSync[action.payload],
            status: 'processing',
          },
        },
      };
    }

    case DATA_INTEGRATION_SYNC_COMPLETE: {
      return {
        ...state,
        integrationSync: {
          ...state.integrationSync,
          [action.payload]: {
            ...state.integrationSync[action.payload],
            status: 'complete',
            watch: false,
          },
        },
      };
    }

    case DATA_INTEGRATION_SYNC_FAILED: {
      return {
        ...state,
        integrationSync: {
          ...state.integrationSync,
          [action.payload]: {
            ...state.integrationSync[action.payload],
            status: 'failed',
          },
        },
      };
    }

    case DATA_BULK_PROGRESS:
      return {
        ...state,
        bulk: {
          ...state.bulk,
          percent: get(action, 'payload', state.bulk.percent),
          message: get(action.meta, 'message', state.bulk.message),
        },
      };

    case DATA_BULK_CANCEL:
      return {
        ...state,
        bulk: {
          ...initialState.bulk,
        },
      };

    case DATA_BULK_COMPLETE:
      return {
        ...state,
        bulk: {
          ...state.bulk,
          running: false,
          complete: true,
          percent: 100,
          result: action.payload,
        },
      };

    case DATA_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case DATA_LOADING_RELATED:
      return {
        ...state,
        loadingRelated: action.payload,
      };

    case DATA_PATCH_RECORD:
      return {
        ...state,
        record: { ...state.record, ...action.payload },
      };

    default:
      return state;
  }
}

function dispatchChildAction(childId, method, args) {
  return (dispatch, getState) => {
    const childDispatch = (action) => {
      return dispatch({
        ...action,
        meta: { ...(action.meta || {}), childId },
      });
    };
    const childGetState = () => {
      const state = getState();
      return { ...state, data: state.children[childId] };
    };
    const action = method.call(actions, ...args);
    if (typeof action === 'function') {
      return action(childDispatch, childGetState);
    }
    return childDispatch(action);
  };
}

// Appends actions to handle child scoped dispatching
function appendChildActions() {
  const actionNames = Object.keys(actions);
  for (const name of actionNames) {
    const childName = `child${name[0].toUpperCase()}${name.slice(1)}`;
    actions[childName] = function (childId, ...args) {
      return dispatchChildAction(childId, actions[name], args);
    };
    Object.defineProperty(actions[childName], 'name', {
      value: childName,
    });
  }
}

function getSelectionCount(sel, coll) {
  if (sel.all) {
    return coll.count - Object.keys(sel.except).length;
  } else {
    return Object.keys(sel.records).length;
  }
}

function getSelectionRange(fromId, toId, coll) {
  const range = {};
  let inRange = false;
  for (const record of coll.results) {
    if (record.id === fromId || record.id === toId) {
      if (inRange) {
        range[record.id] = true;
        break;
      }
      inRange = true;
    }
    if (inRange) {
      range[record.id] = true;
    }
  }
  return range;
}

export function replaceLocationQuery(store, nextState, replace) {
  const { data } = store.getState();
  const { BASE_URI } = process.env;
  if (data.locationQuery) {
    if (!nextState.location.query) {
      let nextUri = locationWithQuery(nextState.location, data.locationQuery);
      if (nextUri.indexOf(BASE_URI) === 0) {
        nextUri = nextUri.replace(BASE_URI, '');
      }
      replace(nextUri);
      return true;
    } else {
      // TODO: we used to compare data.locationQuery with nextState.location.query, failed in some cases
    }
  }
  return false;
}

// Bulk data export
function runExport(args, data = []) {
  const {
    getState,
    model,
    params,
    onCancel,
    onError,
    lastId = null,
    page = 1,
    totalCount = 0,
    retries = 0,
  } = args;

  const query = getExportQuery(params, getState, lastId, page);

  if (!query) {
    onError('There is nothing to export');
    return;
  }

  const request =
    !params.method || params.method === 'get'
      ? api.post(`/data/$get/${model}`, { ...query })
      : api[params.method](`/data/${model}`, { ...query });

  return request
    .then((result) => {
      // May be no results
      if (!result.count && !data.length) {
        onError('There is nothing to export');
        return;
      }
      // May be canceled while running
      if (getState().data.bulk.running !== true) {
        onCancel();
        return;
      }
      // Update data from result
      handleExportData(args, result, data);
      // Update progress state
      const resultCount = totalCount || result.count;
      handleExportProgress(args, resultCount, data);
      // Complete when pages run out
      if (result.results.length === 0) {
        handleExportComplete(args, data);
        return;
      }
      // Next page
      const lastId = result.results[result.results.length - 1].id;
      return runExport(
        {
          ...args,
          totalCount: resultCount,
          page: page + 1,
          lastId,
          retries: 0,
        },
        data,
      );
    })
    .catch((err) => {
      console.error(err);
      if (retries > 10) {
        onError(err.message);
        return;
      }
      return runExport({ ...args, retries: retries + 1 }, data);
    });
}

function getExportQuery(params, getState, lastId = null, page = 1) {
  const { format, query: paramsQuery = {} } = params;

  if (format !== 'csv') {
    paramsQuery.expand = [];
  }

  const { client, data } = getState();

  let query = {
    ...getClientLocaleAndCurrencyParams(
      client,
      params.allLocales,
      params.allCurrencies,
    ),
    limit: BULK_PAGE_LIMIT,
    ...paramsQuery,
    sort: {
      id: -1,
      ...paramsQuery.sort,
    },
    ...(lastId
      ? paramsQuery.conditions
        ? {
            conditions: {
              $and: [
                ...(paramsQuery.conditions.$and || []),
                { id: { $lt: lastId } },
              ],
            },
          }
        : { $and: [...(paramsQuery.$and || []), { id: { $lt: lastId } }] }
      : params.page
      ? {
          page: page,
        }
      : undefined),
  };

  switch (params.type) {
    case 'page': {
      query.$and = [
        ...(query.$and || []),
        { id: { $in: data.collection.results.map((result) => result.id) } },
      ];
      break;
    }

    case 'search': {
      query = {
        ...data.filterQuery,
        ...query,
        search: data.query.search,
        where: {
          ...(query.where || {}),
          ...(data.query.where || {}),
        },
      };
      break;
    }

    case 'date': {
      query.$and = [
        ...(query.$and || []),
        { date_created: { $gte: params.date_start } },
        { date_created: { $lte: params.date_end } },
      ];
      break;
    }

    case 'selected': {
      // The records bulk operations and app actions select
      const selectionQuery = getSelectionQuery(data);

      if (!selectionQuery) {
        return;
      }

      // Selection first, then the export's paging, which is in conditions
      // when the export has its own
      const $and = [...(selectionQuery.$and || []), ...(query.$and || [])];
      const conditionsAnd = [
        ...(selectionQuery.conditions?.$and || []),
        ...(query.conditions?.$and || []),
      ];

      query = {
        ...query,
        ...selectionQuery,
        where: {
          ...(query.where || {}),
          ...(selectionQuery.where || {}),
        },
        ...((query.conditions || selectionQuery.conditions) && {
          conditions: {
            ...query.conditions,
            ...selectionQuery.conditions,
          },
        }),
      };

      if ($and.length > 0) {
        query.$and = $and;
      }

      if (conditionsAnd.length > 0) {
        query.conditions.$and = conditionsAnd;
      }
      break;
    }

    case 'all':
    default:
      break;
  }

  return query;
}

function handleExportProgress({ onProgress, params }, resultCount, data) {
  const dataCount = params.onCountRowsExported
    ? params.onCountRowsExported(data)
    : data.length;

  let percent = Math.ceil((dataCount / resultCount) * 100);

  if (percent > 100) {
    percent = 100;
  }

  onProgress(percent);
}

function handleExportData({ params }, result, data) {
  switch (params.format) {
    case 'json': {
      result.results.forEach((result) => {
        data.push(generateJSONRows(result, params));
      });

      break;
    }

    case 'csv':
    default: {
      // CSV header first
      if (data.length === 0) {
        data.push(params.csvFields.map((field) => field.label));
      }

      result.results.forEach((result) => {
        data.push(...generateCSVRows(result, params));
      });

      break;
    }
  }
}

function handleExportComplete({ getState, params, onComplete }, data = []) {
  let finalData;
  let filename;
  let mimetype;
  let extype;

  const { client } = getState();
  const now = Date.now();

  switch (params.type) {
    case 'page':
      extype = `current-page-${formatDate(now, 'isoDate')}`;
      break;
    case 'search':
      extype = `current-search-${formatDate(now, 'isoDate')}`;
      break;
    case 'date':
      extype = `by-date-${formatDate(
        params.date_start,
        'isoDate',
      )}-to-${formatDate(params.date_end, 'isoDate')}`;
      break;
    case 'selected':
      extype = `selected-${formatDate(now, 'isoDate')}`;
      break;
    default:
    case 'all':
      extype = `all-${formatDate(now, 'isoDate')}`;
      break;
  }

  const filenameId = params.filename || snakeCase(params.model);

  switch (params.format) {
    case 'json':
      filename = `${client.id}-${filenameId}-${extype}.json`;
      mimetype = 'application/json';
      finalData = `${JSON.stringify(data)}`;
      break;
    case 'csv':
    default:
      filename = `${client.id}-${filenameId}-${extype}.csv`;
      mimetype = 'text/csv';
      finalData = CSV.stringify(data);
      break;
  }

  onComplete({
    data: finalData,
    filename,
    mimetype,
  });
}

function generateJSONRows(result, params) {
  // Generate values for each JSON field
  return params.csvFields.reduce((acc, field) => {
    if (typeof field.export === 'function') {
      const key = Array.isArray(field.key)
        ? field.label.toLowerCase()
        : field.key;

      // do not format undefined values
      if (result[key] !== undefined) {
        try {
          acc[key] = field.export(result, params.format, params);
        } catch (err) {
          // Oops
          console.warn(err);
        }
      }
    }

    return acc;
  }, result);
}

function generateCSVRows(result, params) {
  // Generate values for each CSV field
  const values = params.csvFields.map((field) => {
    let value;

    if (typeof field.export === 'function') {
      try {
        value = field.export(result, params.format, params);
      } catch (err) {
        // Oops
        console.warn(err);
      }
    } else if (field.value !== undefined) {
      ({ value } = field);
    } else {
      value = result[field.key];
    }

    return value;
  });

  // Generate row matrix from values
  const rows = [[]];

  values.forEach((value, topIndex) => {
    if (Array.isArray(value)) {
      value.forEach((val, valIndex) => {
        if (rows[valIndex] === undefined) {
          rows[valIndex] = Array.from({ length: values.length }).fill('');
        }

        rows[valIndex][topIndex] = val !== undefined ? val : '';
      });
    } else {
      rows[0].push(value !== undefined ? value : '');
    }
  });

  return rows;
}

/**
 * Query of the selected records: the list query without the keys that shape
 * the page, with the selected ids merged into its top-level $and
 *
 * @param {{ selection: object, query: object }} args
 * @returns {object | undefined} undefined when nothing is selected
 */
export function getSelectionQuery({ selection, query }) {
  const selectionQuery = omit(query, PAGE_QUERY_KEYS);

  if (selection.all) {
    const exceptIds = Object.keys(selection.except);

    if (exceptIds.length > 0) {
      selectionQuery.$and = [
        ...(selectionQuery.$and || []),
        { id: { $nin: exceptIds } },
      ];
    }
  } else {
    const recordIds = Object.keys(selection.records);

    if (recordIds.length > 0) {
      selectionQuery.$and = [
        ...(selectionQuery.$and || []),
        { id: { $in: recordIds } },
      ];
    } else {
      // Nothing selected
      return;
    }
  }

  return selectionQuery;
}

// Bulk query from selection
function getBulkQuery(getState, sortKey, lastId = null) {
  const {
    data: { selection, query },
  } = getState();

  const selectionQuery = getSelectionQuery({ selection, query });

  if (!selectionQuery) {
    return;
  }

  const bulkQuery = {
    ...selectionQuery,
    sort: `${sortKey} desc`,
    limit: null,
    page: 1,
  };

  // Selection first, then the paging, so a key of the list query is kept
  if (lastId) {
    bulkQuery.$and = [
      ...(bulkQuery.$and || []),
      { [sortKey]: { $lt: lastId } },
    ];
  }

  return bulkQuery;
}

/**
 * Bulk query handler
 *
 * @param {object} args
 * @param {(pageResult: object) => void} onData
 * @param {(resultCount: number) => any} onFinished
 * @returns {Promise<void>}
 */
function runBulkQuery(args, onData, onFinished) {
  const {
    model,
    bulkParams,
    getState,
    onCancel,
    onComplete,
    onError,
    lastId = null,
    fields = null,
    lastCount = 0,
    totalCount = 0,
    retries = 0,
  } = args;

  const sortKey = (bulkParams ? bulkParams.sortKey : false) || 'id';

  const query = getBulkQuery(getState, sortKey, lastId);

  if (!query) {
    onError('There is nothing selected');
    return;
  }

  const queryArg = args.query || {};

  return api
    .post('/data/:batch', {
      data: {
        method: 'get',
        url: model,
        data: {
          ...query,
          ...queryArg,
          fields,
        },
      },
    })
    .then(({ data: pageResult }) => {
      // May be no results
      if (!lastId && !pageResult.count) {
        onError('There is nothing selected');
        return;
      }

      // May be canceled while running
      if (getState().data.bulk.running !== true) {
        onCancel();
        return;
      }

      // Update progress state
      const resultCount = totalCount || pageResult.count;
      const progressCount = lastCount + pageResult.results.length;

      runBulkUpdateProgress(args, resultCount, progressCount);

      // Update data from result
      return Promise.resolve(pageResult)
        .then(onData)
        .then(() => {
          // Complete when pages run out
          if (pageResult.results.length === 0) {
            if (typeof onFinished === 'function') {
              const result = onFinished(resultCount);
              onComplete(result);
              return result;
            }

            onComplete();
            return;
          }

          // Next page
          return runBulkQuery(
            {
              ...args,
              totalCount: resultCount,
              lastId:
                pageResult.results[pageResult.results.length - 1][sortKey],
              lastCount: progressCount,
              retries: 0,
            },
            onData,
            onFinished,
          );
        });
    })
    .catch((err) => {
      console.error(err);

      if (retries > 10) {
        onError(err.message);
        return;
      }

      return runBulkQuery(
        { ...args, retries: retries + 1 },
        onData,
        onFinished,
      );
    });
}

// Bulk progress handler
function runBulkUpdateProgress({ onProgress }, resultCount, progressCount) {
  let percent = Math.ceil((progressCount / resultCount) * 100);

  if (percent > 100) {
    percent = 100;
  }

  onProgress(percent);
}

// Bulk data import
async function runImport(args) {
  const {
    getState,
    model,
    params,
    onProgress,
    onComplete,
    onCancel,
    onError,
    index = 0,
    errors = [],
    counts = {},
    retries = 0,
  } = args;

  const {
    data,
    csvTransform,
    idTransform,
    updateTransform,
    recordTransform,
    onMessage,
  } = params;

  // Init counts
  counts.succeeded = counts.succeeded || 0;
  counts.updated = counts.updated || 0;
  counts.ignored = counts.ignored || 0;

  // May be canceled while running
  if (getState().data.bulk.running !== true) {
    onCancel();
    return;
  }

  // Perform insert or update
  try {
    const trans = await csvTransform(
      data[index],
      data[index - 1],
      data[index + 1],
    );

    if (csvTransformHasData(trans)) {
      const recordId = await idTransform(
        data[index],
        data[index - 1],
        data[index + 1],
      );

      const importModel = trans.model || model;

      // Ignore when not overwriting
      const existing = !recordId
        ? false
        : await api.get(`/data/${importModel}/{id}`, {
            id: recordId,
          });

      try {
        if (!existing || params.overwrite) {
          trans.data = updateTransform
            ? await updateTransform(existing, trans.data, trans.orig)
            : trans.data;

          const importMethod = existing ? 'put' : 'post';

          const importUrl = existing
            ? `/data/${importModel}/{id}`
            : `/data/${importModel}`;

          if (onMessage) {
            onProgress(undefined, onMessage(trans.data));
          }

          const result = await api[importMethod](importUrl, {
            ...trans.data,
            id: recordId,
            // Hack: $set doesn't work for post
            ...(existing ? {} : trans.data.$set || {}),
          });

          // Track errors
          if (result.errors) {
            errors.push({
              data: trans.data,
              errors: result.errors,
            });
          } else {
            // Assign id to data
            data[index].id = result.id;
            data[index].record = result;

            // Secondary transform
            if (recordTransform) {
              const recordErrors = await recordTransform(
                `/data/${importModel}/{id}`,
                data[index],
                result,
                data[index + 1],
                data[index - 1],
              );

              if (recordErrors) {
                errors.push(...recordErrors);
              }
            }

            counts.succeeded += 1;

            if (existing) {
              counts.updated += 1;
            }
          }
        } else {
          if (existing) {
            data[index].id = existing.id;
            data[index].record = existing;
          }

          counts.ignored += 1;
        }
      } catch (err) {
        console.error(err);

        if (retries > 10) {
          errors.push({
            data: {},
            errors: {
              'Request error': {
                message: err.message || err,
              },
            },
          });
        } else {
          return runImport({ ...args, retries: retries + 1 });
        }
      }
    }

    // Update progress percent
    let percent = Math.ceil(((index + 1) / data.length) * 100).toFixed(0);

    if (percent > 100) {
      percent = 100;
    }

    onProgress(percent);

    // Continue
    if (data.length > index + 1) {
      await new Promise((resolve) => setTimeout(resolve, 1));

      return runImport({
        ...args,
        index: index + 1,
        errors,
        counts,
        retries: 0,
      });
    }

    // Complete
    onComplete({
      errors,
      counts,
    });
  } catch (err) {
    console.error(err);

    if (retries > 10) {
      onError(err.message);
      return;
    }

    return runImport({ ...args, retries: retries + 1 });
  }
}

function csvTransformHasData(trans) {
  if (!trans || !trans.data) {
    return false;
  }
  for (const key in trans.data) {
    if (trans.data[key] && typeof trans.data[key] !== 'object') {
      return true;
    }
  }
  return false;
}

// Bulk update
function runUpdateSelected(args) {
  const { model, handler } = args;
  return runBulkQuery(
    {
      ...args,
      query: {
        limit: 100,
      },
    },
    async (pageResult) => {
      const batchData = [];
      pageResult.results.forEach((record) => {
        const update = handler(record);
        if (update) {
          if (update instanceof Array) {
            update.forEach((up) =>
              batchData.push({
                url: `${model}/${record.id}`,
                ...up,
              }),
            );
          } else {
            batchData.push({
              url: `${model}/${record.id}`,
              ...update,
            });
          }
        }
      });
      if (batchData.length > 0) {
        await api.put('/data/:batch', batchData);
      }
    },
    (resultCount) => resultCount,
  );
}

// Bulk delete
function runDeleteSelected(args) {
  const { model, cancelOrders, onNotDeleted } = args;

  // Ids of all records failed to cancel or delete, a page may be retried
  const allNotCanceledIds = new Set();
  const allNotDeletedIds = new Set();

  return runBulkQuery(
    {
      ...args,
      // Fields to check if orders can be canceled
      ...(cancelOrders && { fields: ORDER_CANCELABLE_FIELDS }),
      query: {
        limit: 100,
        fields: 'id',
      },
    },
    async (pageResult) => {
      if (pageResult.results.length > 0) {
        // Cancel orders counted in sales first so they're not counted anymore
        const cancelRecords = cancelOrders
          ? pageResult.results.filter(isOrderCancelable)
          : [];

        // Orders failed to cancel are not deleted
        const notCanceledIds = new Set(cancelRecords.map(({ id }) => id));

        if (cancelRecords.length > 0) {
          const cancelResults = await api.put(
            '/data/:batch',
            cancelRecords.map((record) => ({
              method: 'put',
              url: `${model}/${record.id}`,
              data: { canceled: true },
            })),
          );

          for (const result of Object.values(cancelResults || {})) {
            if (result?.canceled) {
              notCanceledIds.delete(result.id);
              allNotCanceledIds.delete(result.id);
            }
          }

          notCanceledIds.forEach((id) => allNotCanceledIds.add(id));
        }

        const deleteRecords = pageResult.results.filter(
          (record) => !notCanceledIds.has(record.id),
        );

        if (deleteRecords.length > 0) {
          const deleteResults = Object.values(
            (await api.put(
              '/data/:batch',
              deleteRecords.map((record) => ({
                method: 'delete',
                url: `${model}/${record.id}`,
                query: args.query,
              })),
            )) || {},
          );

          // Results are in the order of the batch requests
          deleteRecords.forEach(({ id }, index) => {
            const result = deleteResults[index];

            if (result?.errors || result?.error) {
              allNotDeletedIds.add(id);
            } else {
              allNotDeletedIds.delete(id);
            }
          });
        }
      }
    },
    (resultCount) => {
      const notDeletedCount = allNotCanceledIds.size + allNotDeletedIds.size;

      if (notDeletedCount > 0) {
        onNotDeleted(notDeletedCount, allNotCanceledIds.size);
      }
      return resultCount - notDeletedCount;
    },
  );
}

// Bulk coupon code generation progress
function runCouponCodeGenerationProgress(gen, args) {
  const { onError, onProgress, onComplete } = args;

  if (!gen) {
    onError('Unable to generate coupon codes');
    return;
  }
  if (gen.errors) {
    onError(Object.keys(gen.errors)[0].message);
    return;
  }
  if (gen.error) {
    onError(gen.error);
    return;
  }

  return new Promise((resolve) => {
    setTimeout(async () => {
      const genUpdated = await api.get('/data/coupons:generations/{id}', {
        id: gen.id,
        expand: 'codes:1',
      });

      const progressPercent = (
        (genUpdated.codes.count / genUpdated.count) *
        100
      ).toFixed(0);
      onProgress(progressPercent);

      if (progressPercent >= 100) {
        onComplete();
        resolve();
        return;
      }

      await runCouponCodeGenerationProgress(genUpdated, args);
      resolve();
    }, 1000);
  });
}

async function runIntegrationProgressCheck(integrationId, args) {
  // May be unwatched while running
  const integrationState = args.getState().data.integrationSync[integrationId];

  if (!integrationState?.watch) {
    return;
  }

  const integrationSetting = await api.get(
    `/data/settings/integrations/services/${integrationId}`,
  );

  switch (integrationSetting.sync_status) {
    case 'complete':
      args.onComplete();
      return;

    case 'failed':
      args.onError(integrationSetting.sync_error);
      return;

    case 'processing':
      args.onProcessing();
      break;

    default:
      break;
  }

  await new Promise((resolve) => {
    setTimeout(resolve, 3000);
  });

  return runIntegrationProgressCheck(integrationId, args);
}
